import { forwardRef } from 'react'

import { styled, css } from 'styled-components'

import Icon from 'core/components/Icon'
import { IconName } from 'core/components/Icon/iconDefinitions'
import Tooltip from 'core/components/lib/Tooltip'
import variables from 'core/styles/variables'

import { withLabel, withFormValue } from 'admin/decorators'
import useAsyncOnChange from 'admin/decorators/withFormValue/useAsyncOnChange'
import { humanizeKey } from 'admin/helpers'

type Props = {
  formKey: string
  isFocused?: boolean
  width?: string
  onEnter?: (event: any) => void
  onEscape?: (event: any) => void
  onArrowUp?: (event: any) => void
  onArrowDown?: (event: any) => void
  onKeyDown?: (event: any) => void
  placeholder?: string
  type?: string
  nullable?: boolean
  delay?: number
  bare?: boolean
  iconName?: IconName
  iconColor?: string
  bottomBorderOnly?: boolean
  tooltipContent?: any
  value?: any
  onChange?: (value: any) => void
  label?: string
  backgroundColor?: string
}

const Input = forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    formKey,
    isFocused,
    width,
    onEnter,
    onEscape,
    onArrowUp,
    onArrowDown,
    onKeyDown,
    placeholder,
    type,
    nullable,
    delay,
    bare,
    iconName,
    iconColor,
    bottomBorderOnly,
    tooltipContent,
    backgroundColor,
    ...rest
  } = props

  // todo: figure out how to separate this async stuff out and opt into it

  const { value, onChange } = useAsyncOnChange(props)

  const displayType = type || (formKey === 'password' ? 'password' : 'text')

  const handleKeyDown = (event: any) => {
    if (onKeyDown) onKeyDown(event)

    switch (event.key) {
      case 'Enter':
        if (onEnter) onEnter(event)
        break
      case 'Escape':
        if (onEscape) onEscape(event)
        break
      case 'ArrowUp':
        if (onArrowUp) onArrowUp(event)
        break
      case 'ArrowDown':
        if (onArrowDown) onArrowDown(event)
        break
      default:
        break
    }
  }

  return (
    <Wrapper bottomBorderOnly={bottomBorderOnly}>
      {iconName && <Icon color={iconColor} fontSize='24px' name={iconName} />}
      <StyledInput
        $backgroundColor={backgroundColor}
        isFocused={isFocused}
        key={formKey}
        {...rest}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || humanizeKey(formKey)}
        ref={ref}
        type={displayType}
        value={value || ''}
        width={width || '100%'}
      />
      {tooltipContent ?
        <Tooltip
          animation='shift-away'
          backgroundColor={variables.colorBlueLighten}
          content={tooltipContent}
          hideOnClick
          placement='bottom-end'
          width={648}
        >
          <Icon color={variables.colorBlack60} fontSize='22px' name='help' outlined />
        </Tooltip>
      : null}
    </Wrapper>
  )
})

Input.displayName = 'Input'

export default withFormValue(withLabel(Input))

const Wrapper = styled.span<{ bottomBorderOnly?: boolean }>`
  display: flex;
  position: relative;
  align-items: center;
  border: 1px solid ${variables.colorBlack20};
  border-radius: 4px;
  ${(p) =>
    p.bottomBorderOnly &&
    `
    border: 2px solid ${variables.colorBlack20};
    border-style: none none solid none;
    border-radius: 0;
  `}
`
const focused = css`
  border-color: ${(p) => p.theme.highlight};
  box-shadow: none;
`

const StyledInput = styled.input<{ $backgroundColor?: string; isFocused?: boolean }>`
  box-sizing: border-box;
  display: block;
  border: none;
  border-radius: 4px;
  background-color: ${(p) => p.$backgroundColor || 'white'};
  padding: 8px 12px;
  width: ${(p) => p.width};
  color: ${(p) => p.theme.text};
  font-size: 16px;

  &::placeholder {
    color: ${variables.colorBlack50};
  }

  &:disabled {
    border-color: ${(p) => p.theme.border};
    background-color: ${(p) => p.theme.offset};
    color: #888;
  }

  ${(p) => p.isFocused && focused}

  &:focus {
    ${focused}
  }
`
